import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  GraduationCap,
  CalendarRange,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { listCourses, getCourse } from '../services/coursesApi';
import type { CourseSummary, CourseDetail } from '../services/coursesApi';
import { useCourseStore } from '../stores/courseStore';
import { useShellTitle } from '../components/layout/ShellTitleContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import WeekCard from '../components/course/WeekCard';
import IssuedReportCard from '../features/learnerPlan/report/IssuedReportCard';
import {
  formatDuration,
  weekEstimatedMinutes,
  weekHasLessons,
  weekLessonProgress,
} from '../components/course/weekPresentation';

// A single summary stat in the dashboard header strip.
function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof BookOpen;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-ws-accent-quiet text-ws-accent">
        <Icon size={17} />
      </span>
      <div className="leading-tight">
        <div className="text-sm font-semibold text-ws-ink tabular-nums">{value}</div>
        <div className="text-[11px] text-ws-ink-3">{label}</div>
      </div>
    </div>
  );
}

// Skeleton placeholder shown while a course's weeks load.
function WeekSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-ws-tile-border bg-ws-tile shadow-sm">
      <div className="aspect-[16/9] w-full animate-pulse bg-ws-surface-2" />
      <div className="space-y-3 p-4">
        <div className="h-4 w-3/4 animate-pulse rounded bg-ws-surface-2" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-ws-surface-2" />
        <div className="h-1.5 w-full animate-pulse rounded-full bg-ws-surface-2" />
      </div>
    </div>
  );
}

// Dashboard: lists the user's courses; once a course is selected (via the URL
// /courses/:courseId or by picking one) shows its 14-week calendar as banner
// tiles with per-week progress and the session/due plan.
export default function Dashboard() {
  const { courseId: routeCourseId } = useParams();
  const navigate = useNavigate();
  const { currentCourseId, setCurrentCourseId } = useCourseStore();
  const [courses, setCourses] = useState<CourseSummary[]>([]);
  const [detail, setDetail] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  // Distinguish a real load failure from an empty result so the empty state
  // isn't shown misleadingly when the network/API is actually down.
  const [listError, setListError] = useState(false);
  const [detailError, setDetailError] = useState(false);
  // Bump to force a re-fetch from the error-state Retry buttons.
  const [reloadKey, setReloadKey] = useState(0);

  // The active course id: explicit route param wins, else the persisted choice.
  const activeCourseId = routeCourseId || currentCourseId || null;

  useShellTitle(detail?.course.title || 'Dashboard');

  const retryList = useCallback(() => {
    setLoading(true);
    setListError(false);
    setReloadKey((k) => k + 1);
  }, []);

  const retryDetail = useCallback(() => {
    setDetailError(false);
    setReloadKey((k) => k + 1);
  }, []);

  // Load the course list once (and on explicit retry).
  useEffect(() => {
    let cancelled = false;
    listCourses()
      .then((cs) => {
        if (cancelled) return;
        setCourses(cs);
        // Auto-select the only/first course so the nav populates on first visit.
        if (!activeCourseId && cs.length > 0) setCurrentCourseId(cs[0].id);
      })
      .catch(() => {
        if (cancelled) return;
        setCourses([]);
        setListError(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // Keep the shared course scope in sync with a /courses/:id deep-link.
  useEffect(() => {
    if (routeCourseId && routeCourseId !== currentCourseId) {
      setCurrentCourseId(routeCourseId);
    }
  }, [routeCourseId, currentCourseId, setCurrentCourseId]);

  // Load the active course's week tree.
  useEffect(() => {
    if (!activeCourseId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(false);
    getCourse(activeCourseId)
      .then((d) => !cancelled && setDetail(d))
      .catch(() => {
        if (cancelled) return;
        setDetail(null);
        setDetailError(true);
      })
      .finally(() => !cancelled && setDetailLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeCourseId, reloadKey]);

  const weeks = useMemo(() => detail?.weeks ?? [], [detail]);

  // Aggregate stats for the header strip.
  const stats = useMemo(() => {
    let completed = 0;
    let total = 0;
    let minutes = 0;
    let lectureWeeks = 0;
    for (const w of weeks) {
      const p = weekLessonProgress(w);
      completed += p.completed;
      total += p.total;
      minutes += weekEstimatedMinutes(w);
      if (weekHasLessons(w)) lectureWeeks += 1;
    }
    return {
      completed,
      total,
      minutes,
      lectureWeeks,
      pct: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [weeks]);

  if (loading) {
    return (
      <div className="grid place-items-center h-full p-10" role="status" aria-live="polite">
        <Spinner size={22} label="Loading your courses…" />
      </div>
    );
  }

  // Course list failed to load — distinct from "no courses", with a retry.
  if (listError) {
    return (
      <div className="grid place-items-center h-full p-10">
        <div className="max-w-sm text-center" role="alert">
          <span className="mx-auto grid size-16 place-items-center rounded-2xl bg-ws-danger/10 text-ws-danger">
            <AlertTriangle size={30} />
          </span>
          <h2 className="mt-4 text-lg font-semibold text-ws-ink">Couldn’t load your courses</h2>
          <p className="mt-1 text-sm text-ws-ink-3">
            Something went wrong reaching the course service. Check your connection
            and try again.
          </p>
          <Button variant="secondary" size="md" onClick={retryList} className="mt-4">
            <RefreshCw size={15} /> Try again
          </Button>
        </div>
      </div>
    );
  }

  // No courses at all — friendly empty state.
  if (courses.length === 0) {
    return (
      <div className="grid place-items-center h-full p-10">
        <div className="text-center max-w-sm">
          <span className="mx-auto grid size-16 place-items-center rounded-2xl bg-ws-accent-quiet text-ws-accent">
            <GraduationCap size={32} />
          </span>
          <h2 className="mt-4 text-lg font-semibold text-ws-ink">No courses yet</h2>
          <p className="mt-1 text-sm text-ws-ink-3">
            You are not enrolled in any courses. Once a course is published it will
            appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ws-page">
      {/* The progress report the instructor issued to this student, if any.
          Renders nothing at all until one exists — see IssuedReportCard. It
          sits above the course switcher because a report an instructor has
          just handed over is the most time-sensitive thing on this page. */}
      <IssuedReportCard />

      {/* Course switcher (when enrolled in more than one) */}
      {courses.length > 1 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {courses.map((c) => {
            const active = c.id === activeCourseId;
            return (
              <button
                key={c.id}
                onClick={() => navigate(`/courses/${c.id}`)}
                aria-pressed={active}
                className={`rounded-md border px-3.5 py-1.5 text-sm transition-colors ${
                  active
                    ? 'border-ws-accent bg-ws-accent-quiet text-ws-accent-ink font-medium'
                    : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:bg-ws-surface-2 hover:border-ws-border-strong'
                }`}
              >
                {c.code ? `${c.code} — ${c.title}` : c.title}
              </button>
            );
          })}
        </div>
      )}

      {/* Header / hero */}
      <header className="mb-6 overflow-hidden rounded-2xl border border-ws-border bg-ws-surface shadow-sm">
        <div
          className="h-1.5 w-full"
          style={{
            backgroundImage:
              'linear-gradient(90deg, var(--ws-accent) 0%, var(--ws-dark-teal) 55%, var(--ws-gold) 100%)',
          }}
        />
        <div className="p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              {detail?.course.code && (
                <div className="text-[11px] font-semibold uppercase tracking-wider text-ws-accent">
                  {detail.course.code}
                </div>
              )}
              <h1 className="text-xl font-bold tracking-tight text-ws-ink">
                {detail?.course.title || 'Course'}
              </h1>
              {detail?.course.description && (
                <p className="mt-1.5 max-w-2xl text-sm text-ws-ink-3">
                  {detail.course.description}
                </p>
              )}
            </div>
          </div>

          {/* Summary stats */}
          {!detailLoading && stats.total > 0 && (
            <div className="mt-5 flex flex-wrap gap-x-8 gap-y-4 border-t border-ws-border pt-5">
              <Stat icon={CheckCircle2} label="Lessons completed" value={`${stats.completed}/${stats.total}`} />
              <Stat icon={CalendarRange} label="Weeks of content" value={`${stats.lectureWeeks}`} />
              {stats.minutes > 0 && (
                <Stat icon={Clock} label="Estimated study time" value={formatDuration(stats.minutes)} />
              )}
              <div className="flex min-w-[160px] flex-1 flex-col justify-center">
                <div className="flex items-center justify-between text-[11px] text-ws-ink-3">
                  <span>Overall progress</span>
                  <span className="tabular-nums font-medium text-ws-ink-2">{stats.pct}%</span>
                </div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-ws-surface-2">
                  <div
                    className="ws-progress-fill h-full rounded-full"
                    style={{
                      width: `${stats.pct}%`,
                      backgroundImage:
                        'linear-gradient(90deg, var(--ws-accent) 0%, var(--ws-gold) 160%)',
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Loading skeletons */}
      {detailLoading && (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          role="status"
          aria-label="Loading course schedule"
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <WeekSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Course detail failed to load — distinct from "no weeks yet". */}
      {!detailLoading && detailError && (
        <Card className="p-10 text-center" role="alert">
          <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-ws-danger/10 text-ws-danger">
            <AlertTriangle size={24} />
          </span>
          <p className="mt-3 text-sm text-ws-ink-2">
            We couldn’t load this course’s schedule just now.
          </p>
          <Button variant="secondary" size="sm" onClick={retryDetail} className="mt-3">
            <RefreshCw size={14} /> Try again
          </Button>
        </Card>
      )}

      {/* Empty content */}
      {!detailLoading && !detailError && weeks.length === 0 && (
        <Card className="p-10 text-center">
          <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-ws-surface-2 text-ws-ink-3">
            <BookOpen size={26} />
          </span>
          <p className="mt-3 text-sm text-ws-ink-3">
            This course has no published weeks yet. Check back soon.
          </p>
        </Card>
      )}

      {/* Week grid */}
      {!detailLoading && weeks.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {weeks.map((week, i) => (
            <div
              key={week.id}
              className="ws-fade-up h-full"
              style={{ animationDelay: `${Math.min(i, 11) * 40}ms` }}
            >
              <WeekCard week={week} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
